import { useEffect, useState } from "react";
import { AxiosError } from "axios";
import { Box, Button, Typography, Dialog, DialogContent } from "@mui/material";
import __ from "src/utils/Translations";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useChangeModuleSettings } from "src/Pages/Company/OrganizationStructure/api/useModuleSettings";
import { ModuleSettingsSchema } from "src/Pages/Company/OrganizationStructure/ModuleSettings/moduleSettings.scheme";
import { TModuleSettingsPayload } from "src/Pages/Company/OrganizationStructure/ModuleSettings/moduleSettings.models";
import Field from "src/Pages/Company/OrganizationStructure/ModuleSettings/Form/Field";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import { useHistory } from "react-router-dom";

interface IField {
  name: keyof TModuleSettingsPayload;
  title: string;
  subtitle?: string;
}

const fields: IField[] = [
  {
    name: "cardAndListModuleEnabled",
    title: "Karta pracownika i lista pracowników",
    subtitle:
      "Karta pracownika zawiera wszystkie informacje o pracowniku. Lista pracowników to zbiór wizytówek wszystkich zatrudnionych pracowników.",
  },
  {
    name: "structureAreasOrgChartModuleEnabled",
    title: "Struktura organizacji (wizualizacja)",
    subtitle: "Wizualna prezentacja relacji pomiędzy obszarami w firmie.",
  },
  {
    name: "structureEmployeesOrgChartModuleEnabled",
    title: "Struktura pracowników (wizualizacja)",
    subtitle: "Wizualna prezentacja relacji pomiędzy pracownikami.",
  },
];

const Form = ({
  companyId,
  initialValues,
}: {
  companyId: string;
  initialValues: TModuleSettingsPayload;
}) => {
  const mutation = useChangeModuleSettings(companyId);
  const logStatus = useLogStatus();

  useEffect(() => {
    if (!mutation.error) return;
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "ModuleSettingsForm: changeModuleSettings failed",
      statusDetails: {
        source: "API",
        operation: "changeModuleSettings",
        status: (mutation.error as AxiosError)?.response?.status,
        url: (mutation.error as AxiosError)?.config?.url,
      },
      errorObject:
        mutation.error instanceof Error
          ? mutation.error
          : new Error(String(mutation.error)),
    });
  }, [mutation.error, logStatus]);

  const [showConfirmLeaveModal, setShowConfirmLeaveModal] = useState(false);
  const [nextLocation, setNextLocation] = useState<string | null>(null);
  const history = useHistory();

  const handleClose = () => {
    setShowConfirmLeaveModal(false);
  };

  const methods = useForm<TModuleSettingsPayload>({
    resolver: zodResolver(ModuleSettingsSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    reset,
  } = methods;

  const onSubmit: SubmitHandler<TModuleSettingsPayload> = (data) => {
    mutation.mutate(data);
  };

  const handleModalSaveAction = (shouldSave: boolean) => {
    // if react-router-dom gets new version <= 6.0.0, this should be refactored
    if (shouldSave) {
      handleSubmit(onSubmit)();
      reset();
    } else {
      reset();
    }
    setShowConfirmLeaveModal(() => false);
  };

  // if react-router-dom gets new version <= 6.0.0, this should be refactored
  useEffect(() => {
    const unblock = history.block((location) => {
      if (isDirty) {
        setShowConfirmLeaveModal(true);
        setNextLocation(location.pathname);

        return false;
      }
      return undefined;
    });

    return () => {
      unblock();
    };
  }, [isDirty, history]);

  // if react-router-dom gets new version <= 6.0.0, this should be refactored
  useEffect(() => {
    if (!isDirty && nextLocation) {
      history.push(nextLocation);
    }
    // eslint-disable-next-line
  }, [isDirty, nextLocation]);

  return (
    <FormProvider {...methods}>
      <Dialog open={showConfirmLeaveModal} onClose={handleClose}>
        <DialogContent>
          <Typography sx={{ fontSize: "20px", fontWeight: "fontWeightMedium" }}>
            {__("Zapisz wprowadzone zmiany")}
          </Typography>
          <Typography sx={{ fontSize: "17px", margin: "24px 0" }}>
            {__(
              "Czy chcesz zapisać wprowadzone zmiany przed opuszczeniem strony?"
            )}
          </Typography>
          <Box sx={{ display: "flex", gap: "16px" }}>
            <Button
              onClick={() => handleModalSaveAction(false)}
              variant="outlined"
              size="large"
              sx={{ width: "50%" }}
            >
              {__("Kontynuuj bez zapisywania")}
            </Button>
            <Button
              onClick={() => handleModalSaveAction(true)}
              variant="contained"
              size="large"
              sx={{ width: "50%" }}
            >
              {__("Zapisz zmiany")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography
          sx={{
            fontWeight: "fontWeightMedium",
            fontSize: "20px",
            margin: "0 0 8px 0",
          }}
        >
          {__(
            "Wybierz funkcjonalności, które mają być włączone w Strukturze Organizacji"
          )}
        </Typography>
        <Typography
          sx={{
            fontSize: "16px",
            width: "100%",
            maxWidth: "840px",
          }}
          color="text.secondary"
        >
          {__(
            "Włączając moduł struktura organizacji na edycji firmy Klient otrzymuje automatycznie dostęp do podstawowych danych i funkcjonalności pozwalający na tworzenie struktury,"
          )}
          {__(
            " definiowanie obszarów, które wchodzą w jej skład, opis zatrudnienia pracownika oraz przypisanie jego przełożonego."
          )}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            margin: "24px 0",
          }}
        >
          {fields.map((fieldData) => (
            <Field key={fieldData.name} {...fieldData} control={control} />
          ))}
        </Box>

        <Box>
          <Button
            type="submit"
            disabled={!isDirty}
            sx={{
              display: "block",
              margin: "0 0 0 auto",
            }}
            variant="contained"
            size="small"
            data-testid="os__module-settings-save-button"
          >
            {__("Zapisz zmiany")}
          </Button>
        </Box>
      </form>
    </FormProvider>
  );
};

export default Form;
